import { Asset } from 'app-lib/assetUtils'
import { DEX, IUniRoute } from 'app-lib/dexSelector/types'
import { ProviderType } from 'bzx-common/src/domain/ProviderTypeDictionary'
import { BigNumber } from '@0x/utils'
import { getBaseQuote, IChainConfig, udfFeeds } from './chainCommon'

const assets = [
  Asset.ETH,
  Asset.ARB,
  Asset.FRAX,
  Asset.LINK,
  Asset.USDC,
  Asset.USDT,
  Asset.WBTC,
  Asset.SPELL,
  Asset.MIM,
]

const tradePairs: [Asset, Asset][] = [
  [Asset.ARB, Asset.USDT],
  [Asset.ARB, Asset.USDC],
  [Asset.ARB, Asset.WBTC],
  [Asset.ARB, Asset.ETH],
  [Asset.ETH, Asset.USDT],
  [Asset.ETH, Asset.FRAX],
  [Asset.ETH, Asset.MIM],
  [Asset.ETH, Asset.USDC],
  [Asset.FRAX, Asset.MIM],
  [Asset.LINK, Asset.ETH],
  [Asset.LINK, Asset.FRAX],
  [Asset.LINK, Asset.MIM],
  [Asset.LINK, Asset.SPELL],
  [Asset.LINK, Asset.USDC],
  [Asset.LINK, Asset.USDT],
  [Asset.LINK, Asset.WBTC],
  [Asset.SPELL, Asset.ETH],
  [Asset.SPELL, Asset.FRAX],
  [Asset.SPELL, Asset.MIM],
  [Asset.SPELL, Asset.USDC],
  [Asset.SPELL, Asset.USDT],
  [Asset.SPELL, Asset.WBTC],
  [Asset.USDC, Asset.FRAX],
  [Asset.USDC, Asset.MIM],
  [Asset.USDC, Asset.USDT],
  [Asset.USDT, Asset.FRAX],
  [Asset.USDT, Asset.MIM],
  [Asset.WBTC, Asset.ETH],
  [Asset.WBTC, Asset.FRAX],
  [Asset.WBTC, Asset.MIM],
  [Asset.WBTC, Asset.USDC],
  [Asset.WBTC, Asset.USDT],
]

const route1: IUniRoute = {
  topN: 0,
  topNDirectSwaps: 2,
  topNTokenInOut: 0,
  topNSecondHop: 0,
  topNWithEachBaseToken: 0,
  topNWithBaseToken: 0,
}

const route2: IUniRoute = {
  topN: 0,
  topNDirectSwaps: 2,
  topNTokenInOut: 2,
  topNSecondHop: 0,
  topNWithEachBaseToken: 0,
  topNWithBaseToken: 0,
}

const route3: IUniRoute = {
  topN: 0,
  topNDirectSwaps: 0,
  topNTokenInOut: 2,
  topNSecondHop: 0,
  topNWithEachBaseToken: 0,
  topNWithBaseToken: 0,
}

const uniRouteSettings: Record<string, IUniRoute[]> = {
  FRAXUSDC: [route1, route1, route1],
  FRAXUSDT: [route1, route1, route1],
  FRAXWETH: [route1, route1, route1],
  FRAXWBTC: [route1, route1, route1],
  USDCWETH: [route1, route1, route1],
  USDTWETH: [route2, route2, route2],
  WBTCWETH: [route1, route1, route1],
  LINKUSDC: [route3, route3, route3],
  LINKUSDT: [route3, route3, route3],
  FRAXLINK: [route1, route1, route1],
  LINKWBTC: [route3, route3, route3],
  USDCUSDT: [route1, route1, route1],
  USDCWBTC: [route3, route3, route3],
  USDTWBTC: [route3, route3, route3],
}

// add iTokens as collateral
const collateralAssets: Asset[] = assets.concat(assets.map((asset) => ('I' + asset) as Asset))

const config: IChainConfig = {
  chainDetails: {
    chainId: 42161,
    blockExplorer: 'https://arbiscan.io',
    chainName: 'Arbitrum One',
    shortName: 'Arbitrum',
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: ['https://arb1.arbitrum.io/rpc'],
    blocksPerYear: 0,
  },
  borrowing: { assets, collateralAssets },
  comingSoon: {
    tokens: {},
  },
  contracts: {
    bzx: '0x37407f3178ffe07a6cf5c847f8f680fecf319fab',
    helperProxy: '0xB8329B5458B1E493EFd8D9DA8C3B5E6D68e67C21',
    priceFeed: '0x392b7Baf9dBf56a0AcA52f0Ba8bC1D7451Ef8A4A',
    sweepFees: '0xcbDE8C5603D4bA855a162A450B1d054A02D8448f',
    tokenRegistry: '0x86003099131d83944d826F8016E09CC678789A30',
  },
  dexes: [DEX.SUSHI, DEX.UNISWAPV3],
  dexesDetails: {
    [DEX.SUSHI]: {
      id: 1,
      address: '0x8D13F73973ac2Ec980DB01A5FB781636cD4cDF8A',
    },
    [DEX.UNISWAPV3]: {
      id: 2,
      address: '0xF924981CFD341Fe3f3417f77696e35b68CC6d1Fa',
    },
  },
  features: { dexSelector: true },
  gas: {
    priceDefault: new BigNumber(2).times(10 ** 9),
    amount: { coefficient: 1.2, fallback: 450000 },
  },
  lending: { assets },
  protocol: { version: 4 },
  trading: {
    defaultPair: { baseToken: Asset.ETH, quoteToken: Asset.USDT },
    pairs: tradePairs,
    baseQuote: getBaseQuote(tradePairs),
    pairConfigs: {},
  },
  native: { asset: Asset.ETH, wrapped: Asset.WETH },
  nativeTokenSupported: true,
  compromisedContracts: [],
  icon: { name: 'arbitrum-logo', color: '#96BEDC' },
  chartUDF: udfFeeds.BINANCE,
  rpcUrlDefault: process.env.RPC_URL_ARBITRUM,
  stableCoins: [Asset.USDC, Asset.USDT, Asset.FRAX, Asset.MIM],
  stablecoinForUsdRates: Asset.USDC,
  stats: { assets },
  twitterHandle: '@arbitrum',
  uiDisabled: false,
  uiDisabledReason: '',
  wallets: [ProviderType.MetaMask],
  uniRouteSettings,
}

export default config
